import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getTaxServiceByCode } from '../../../../mockData/orgDocIndex';

/**
 * Tax Deadlines Alert Component
 * Displays overdue and upcoming tax filing deadlines
 *
 * @param {Object} props
 * @param {Array} props.deadlines - List of deadlines
 * @param {Function} props.onRequestService - Handler to request related service
 */
const TaxDeadlinesAlert = ({ deadlines, onRequestService }) => {
  if (!deadlines || deadlines.length === 0) {
    return null;
  }

  const hasOverdue = deadlines.some(d => d.status === 'OVERDUE');

  const handleRequestService = (deadline) => {
    const service = getTaxServiceByCode(deadline.relatedServiceCode);
    if (service && onRequestService) {
      onRequestService(service);
    }
  };

  return (
    <Alert
      severity={hasOverdue ? 'error' : 'warning'}
      sx={{ mb: 3 }}
      icon={hasOverdue ? <ErrorIcon /> : <WarningAmberIcon />}
    >
      <AlertTitle>
        {hasOverdue ? 'Overdue Tax Filing!' : 'Upcoming Tax Deadlines'}
      </AlertTitle>
      <List dense disablePadding>
        {deadlines.map((deadline) => (
          <ListItem key={deadline.id} sx={{ pl: 0, pr: 12 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {deadline.status === 'OVERDUE' ? (
                <ErrorIcon color="error" fontSize="small" />
              ) : (
                <WarningAmberIcon color="warning" fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={deadline.description}
              secondary={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption">
                    Due: {new Date(deadline.dueDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </Typography>
                  <Chip
                    label={deadline.status === 'OVERDUE'
                      ? `${Math.abs(deadline.daysUntilDue)} days overdue`
                      : `${deadline.daysUntilDue} days left`
                    }
                    size="small"
                    color={deadline.status === 'OVERDUE' ? 'error' : 'warning'}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Button
                size="small"
                variant={deadline.status === 'OVERDUE' ? 'contained' : 'outlined'}
                color={deadline.status === 'OVERDUE' ? 'error' : 'warning'}
                onClick={() => handleRequestService(deadline)}
              >
                File Now
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Alert>
  );
};

export default TaxDeadlinesAlert;
